import React from 'react';
import { Typography } from '@material-tailwind/react';
import { PiArrowSquareOut } from 'react-icons/pi';
import {
  getColorClass,
  getWeightClass,
} from 'src/modules/shared/components/atoms/text/text.styles';
import Loading from 'src/modules/shared/components/atoms/loading';
import type { LinkProps } from 'src/modules/shared/types/link.type';
import {
  baseLinkClasses,
  linkUnderlineClasses,
  linkVariantClasses,
} from './link.styles';

/**
 * Renders a navigation link or a text action without button chrome.
 * @param props - Link appearance and mode-specific behavior.
 * @returns An anchor for navigation or a native button for an action.
 */
export const Link: React.FC<LinkProps> = (props) => {
  const {
    children,
    className = '',
    size = 'small',
    color = 'inherit',
    variant = 'primary',
    weight = 'regular',
    underline = 'always',
    truncate = false,
    onClick,
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedBy,
    'aria-busy': ariaBusy,
  } = props;
  const sizeClass = size === 'tiny' ? 'text-xs' : '';
  const variantSize = size === 'tiny' ? undefined : size;
  const colorClasses =
    color === 'inherit' ? linkVariantClasses[variant] : getColorClass(color);
  const shouldTruncateClass = Boolean(truncate);
  const layoutClasses = `${baseLinkClasses.join(' ')} ${shouldTruncateClass ? 'truncate' : ''}`;
  const shouldTruncateText =
    typeof children === 'string' && typeof truncate === 'number';
  const content =
    shouldTruncateText && children.length > truncate
      ? `${children.slice(0, truncate)}...`
      : children;
  const title = typeof children === 'string' && truncate ? children : undefined;
  const decorationClasses = [
    'decoration-solid',
    linkUnderlineClasses[underline],
  ].join(' ');
  const classes = `${getWeightClass(weight)} ${sizeClass} ${decorationClasses} ${colorClasses} ${layoutClasses} ${className}`;

  if (props.mode === 'action') {
    return (
      <Typography
        as="span"
        variant={variantSize}
        className={`inline ${getWeightClass(weight)} ${sizeClass}`}
      >
        <button
          type="button"
          className={`${classes} border-0 bg-transparent p-0 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50`}
          disabled={(props.disabled ?? false) || (props.loading ?? false)}
          aria-busy={props.loading ? true : ariaBusy}
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedBy}
          onClick={onClick}
          title={title}
        >
          {content}
          {props.loading && <Loading size="2xs" inline variant={variant} />}
        </button>
      </Typography>
    );
  }

  const {
    href,
    external = true,
    showExternalIcon = true,
    iconPosition = 'end',
    target,
    rel,
  } = props;
  const computedRel = rel ?? (external ? 'noopener noreferrer' : undefined);
  const computedTarget = target ?? (external ? '_blank' : undefined);

  return (
    <Typography
      as="a"
      href={href}
      target={computedTarget}
      rel={computedRel}
      variant={variantSize}
      className={classes}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      aria-busy={ariaBusy}
      onClick={onClick}
      title={title}
    >
      {iconPosition === 'start' && external && showExternalIcon && (
        <PiArrowSquareOut className="w-4 h-4 flex-shrink-0" />
      )}
      {content}
      {iconPosition === 'end' && external && showExternalIcon && (
        <PiArrowSquareOut className="w-4 h-4 flex-shrink-0" />
      )}
    </Typography>
  );
};
